import React from 'react'
import {
  Box,
  Skeleton,
  Grid,
  Typography,
  FormControlLabel,
  Checkbox,
  Card,
  CardContent,
  CardHeader,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Avatar
} from '@mui/material'

interface LoadingSkeletonProps {
  variant: 'roles' | 'salesReps' | 'cacInfo' | 'userForm' | 'companyForm' | 'userTable' | 'companyTable' | 'productSelector' | 'searchResults' | 'pageHeader' | 'tabContent' | 'dashboardCards' | 'formFields' | 'actionButtons' | 'collapsibleSection' | 'dashboardMetrics' | 'dashboardChart' | 'dashboardActivity' | 'dashboardOverview' | 'userPrimaryInfo' | 'companyPrimaryInfo'
  rows?: number
  columns?: number
}

export function LoadingSkeleton({ variant, rows = 3, columns = 6 }: LoadingSkeletonProps) {
  // Existing variants
  if (variant === 'roles') {
    return (
      <Box>
        <Typography variant="body2" gutterBottom>Role(s)</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {[1, 2, 3, 4, 5].map((i) => (
            <FormControlLabel
              key={i}
              control={<Checkbox disabled />}
              label={<Skeleton width={120} />}
            />
          ))}
        </Box>
      </Box>
    )
  }

  if (variant === 'salesReps') {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" gutterBottom>Sales Representative(s)</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {[1, 2, 3, 4, 5].map((i) => (
            <FormControlLabel
              key={i}
              control={<Checkbox disabled />}
              label={<Skeleton width={150} />}
            />
          ))}
        </Box>
      </Box>
    )
  }

  if (variant === 'cacInfo') {
    return (
      <Grid container spacing={2}>
        <Grid size={{ xs: 6 }}>
          <Typography variant="body2" gutterBottom>Lot #:</Typography>
          <Skeleton variant="rectangular" height={40} />
        </Grid>
        <Grid size={{ xs: 6 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
            <Checkbox disabled />
            <Skeleton width={120} />
          </Box>
        </Grid>
        <Grid size={{ xs: 6 }}>
          <Typography variant="body2" gutterBottom>Dealer Rating:</Typography>
          <Skeleton variant="rectangular" height={40} />
        </Grid>
        <Grid size={{ xs: 6 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
            <Checkbox disabled />
            <Skeleton width={100} />
          </Box>
        </Grid>
        <Grid size={{ xs: 6 }}>
          <Typography variant="body2" gutterBottom>Sales Tax %:</Typography>
          <Skeleton variant="rectangular" height={40} />
        </Grid>
        <Grid size={{ xs: 6 }}>
          <Typography variant="body2" gutterBottom>Purchase:</Typography>
          <Skeleton variant="rectangular" height={40} />
        </Grid>
        <Grid size={{ xs: 6 }}>
          <Typography variant="body2" gutterBottom>Advance Code:</Typography>
          <Skeleton variant="rectangular" height={40} />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Typography variant="body2" gutterBottom>MAM:</Typography>
          <Skeleton variant="rectangular" height={40} />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Typography variant="body2" gutterBottom>RAM:</Typography>
          <Skeleton variant="rectangular" height={40} />
        </Grid>
      </Grid>
    )
  }

  // New comprehensive skeleton variants
  if (variant === 'userForm') {
    return (
      <Grid container spacing={3}>
        {/* Left Column */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ mb: 2 }}>
            <CardHeader
              avatar={<Skeleton variant="circular" width={32} height={32} />}
              title={<Skeleton width={100} />}
              action={<Skeleton variant="circular" width={24} height={24} />}
            />
            <CardContent>
              <Grid container spacing={2}>
                <Grid size={3}>
                  <Skeleton variant="rectangular" height={40} />
                </Grid>
                <Grid size={4}>
                  <Skeleton variant="rectangular" height={40} />
                </Grid>
                <Grid size={2}>
                  <Skeleton variant="rectangular" height={40} />
                </Grid>
                <Grid size={3}>
                  <Skeleton variant="rectangular" height={40} />
                </Grid>
                <Grid size={12}>
                  <Skeleton variant="rectangular" height={40} />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
          
          <Card sx={{ mb: 2 }}>
            <CardHeader
              avatar={<Skeleton variant="circular" width={32} height={32} />}
              title={<Skeleton width={120} />}
              action={<Skeleton variant="circular" width={24} height={24} />}
            />
            <CardContent>
              <Grid container spacing={2}>
                <Grid size={6}>
                  <Skeleton variant="rectangular" height={40} />
                </Grid>
                <Grid size={6}>
                  <Skeleton variant="rectangular" height={60} />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ mb: 2 }}>
            <CardHeader
              avatar={<Skeleton variant="circular" width={32} height={32} />}
              title={<Skeleton width={80} />}
              action={<Skeleton variant="circular" width={24} height={24} />}
            />
            <CardContent>
              <Grid container spacing={2}>
                {[1, 2, 3].map((i) => (
                  <Grid size={12} key={i}>
                    <Skeleton variant="rectangular" height={40} />
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    )
  }

  if (variant === 'companyForm') {
    return (
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ mb: 2 }}>
            <CardHeader
              avatar={<Skeleton variant="circular" width={32} height={32} />}
              title={<Skeleton width={120} />}
            />
            <CardContent>
              <Stack spacing={2}>
                <Skeleton variant="rectangular" height={40} />
                <Skeleton variant="rectangular" height={40} />
                <Skeleton variant="rectangular" height={40} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ mb: 2 }}>
            <CardHeader
              avatar={<Skeleton variant="circular" width={32} height={32} />}
              title={<Skeleton width={80} />}
            />
            <CardContent>
              <Stack spacing={2}>
                <Skeleton variant="rectangular" height={40} />
                <Skeleton variant="rectangular" height={40} />
                <Skeleton variant="rectangular" height={100} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    )
  }

  if (variant === 'userTable' || variant === 'companyTable') {
    return (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {Array.from({ length: columns }).map((_, i) => (
                <TableCell key={i}>
                  <Skeleton width={100} />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.from({ length: rows }).map((_, rowIndex) => (
              <TableRow key={rowIndex}>
                {Array.from({ length: columns }).map((_, colIndex) => (
                  <TableCell key={colIndex}>
                    {colIndex === 0 ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Skeleton variant="circular" width={32} height={32} />
                        <Skeleton width={120} />
                      </Box>
                    ) : (
                      <Skeleton width={colIndex === columns - 1 ? 80 : 100} />
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    )
  }

  if (variant === 'productSelector') {
    return (
      <Box>
        <Skeleton variant="rectangular" height={40} sx={{ mb: 2 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Skeleton width={150} />
          <Skeleton width={80} />
        </Box>
        
        {[1, 2, 3].map((i) => (
          <Card key={i} sx={{ mb: 2 }}>
            <CardHeader
              avatar={<Skeleton variant="circular" width={24} height={24} />}
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Skeleton width={150} />
                  <Skeleton width={40} />
                </Box>
              }
              action={<Skeleton variant="circular" width={24} height={24} />}
            />
            <CardContent>
              <Stack spacing={1}>
                {[1, 2, 3, 4].map((j) => (
                  <Box key={j} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Skeleton variant="rectangular" width={20} height={20} />
                    <Skeleton width={200} />
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Box>
    )
  }

  if (variant === 'searchResults') {
    return (
      <Box>
        {Array.from({ length: rows }).map((_, i) => (
          <Box key={i} sx={{ p: 2, borderBottom: '1px solid #eee' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Skeleton variant="circular" width={40} height={40} />
              <Box sx={{ flex: 1 }}>
                <Skeleton width="60%" />
                <Skeleton width="80%" sx={{ mt: 0.5 }} />
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    )
  }

  if (variant === 'pageHeader') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Skeleton variant="circular" width={32} height={32} />
          <Skeleton width={200} />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} variant="rectangular" width={100} height={36} />
          ))}
        </Box>
      </Box>
    )
  }

  if (variant === 'tabContent') {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton width={150} sx={{ mb: 2 }} />
        <Skeleton width="100%" sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Grid size={{ xs: 12, md: 6 }} key={i}>
              <Skeleton variant="rectangular" height={40} />
            </Grid>
          ))}
        </Grid>
      </Box>
    )
  }

  if (variant === 'dashboardCards') {
    return (
      <Grid container spacing={3}>
        {Array.from({ length: 4 }).map((_, i) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={i}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Skeleton variant="circular" width={40} height={40} />
                  <Box>
                    <Skeleton width={60} />
                    <Skeleton width={100} />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    )
  }

  if (variant === 'dashboardMetrics') {
    return (
      <Grid container spacing={3}>
        {Array.from({ length: 4 }).map((_, i) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={i}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ p: 1, borderRadius: 1, backgroundColor: 'rgba(0,0,0,0.1)' }}>
                    <Skeleton variant="circular" width={24} height={24} />
                  </Box>
                  <Skeleton width={60} />
                </Box>
                <Skeleton variant="text" sx={{ fontSize: '2rem', mb: 1 }} width={80} />
                <Skeleton width={100} />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    )
  }

  if (variant === 'dashboardChart') {
    const chartHeights = [80, 120, 95, 150, 110, 180, 90, 140, 100, 160, 85, 130]
    return (
      <Paper sx={{ p: 3, height: 400 }}>
        <Skeleton variant="text" sx={{ fontSize: '1.25rem', mb: 2 }} width={200} />
        <Box sx={{ height: 320, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'end', height: '100%' }}>
            {Array.from({ length: 12 }).map((_, i) => (
              <Skeleton 
                key={i} 
                variant="rectangular" 
                width={20} 
                height={chartHeights[i]}
                sx={{ borderRadius: 1 }}
              />
            ))}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} width={40} />
            ))}
          </Box>
        </Box>
      </Paper>
    )
  }

  if (variant === 'dashboardActivity') {
    return (
      <Paper sx={{ p: 3, height: 400, overflow: 'hidden' }}>
        <Skeleton variant="text" sx={{ fontSize: '1.25rem', mb: 2 }} width={150} />
        <Box sx={{ height: 'calc(100% - 60px)', overflow: 'hidden' }}>
          <Stack spacing={2}>
            {Array.from({ length: 5 }).map((_, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Skeleton variant="circular" width={32} height={32} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton width="80%" />
                  <Skeleton width="60%" sx={{ mt: 0.5 }} />
                </Box>
                <Skeleton width={60} />
              </Box>
            ))}
          </Stack>
        </Box>
      </Paper>
    )
  }

  if (variant === 'dashboardOverview') {
    return (
      <Box>
        <Skeleton variant="text" sx={{ fontSize: '2rem', mb: 4 }} width={250} />
        
        {/* Metrics Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={i}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ p: 1, borderRadius: 1, backgroundColor: 'rgba(0,0,0,0.1)' }}>
                      <Skeleton variant="circular" width={24} height={24} />
                    </Box>
                    <Skeleton width={60} />
                  </Box>
                  <Skeleton variant="text" sx={{ fontSize: '2rem', mb: 1 }} width={80} />
                  <Skeleton width={100} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Chart and Activity sections */}
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 8 }}>
            <Paper sx={{ p: 3, height: 400 }}>
              <Skeleton variant="text" sx={{ fontSize: '1.25rem', mb: 2 }} width={200} />
              <Box sx={{ height: 320, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'end', height: '100%' }}>
                  {[80, 120, 95, 150, 110, 180, 90, 140, 100, 160, 85, 130].map((height, i) => (
                    <Skeleton 
                      key={i} 
                      variant="rectangular" 
                      width={20} 
                      height={height}
                      sx={{ borderRadius: 1 }}
                    />
                  ))}
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  {Array.from({ length: 6 }).map((_, i) => (
                    <Skeleton key={i} width={40} />
                  ))}
                </Box>
              </Box>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Paper sx={{ p: 3, height: 400, overflow: 'hidden' }}>
              <Skeleton variant="text" sx={{ fontSize: '1.25rem', mb: 2 }} width={150} />
              <Box sx={{ height: 'calc(100% - 60px)', overflow: 'hidden' }}>
                <Stack spacing={2}>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Skeleton variant="circular" width={32} height={32} />
                      <Box sx={{ flex: 1 }}>
                        <Skeleton width="80%" />
                        <Skeleton width="60%" sx={{ mt: 0.5 }} />
                      </Box>
                      <Skeleton width={60} />
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    )
  }

  if (variant === 'formFields') {
    return (
      <Grid container spacing={2}>
        {Array.from({ length: rows }).map((_, i) => (
          <Grid size={{ xs: 12, md: 6 }} key={i}>
            <Skeleton variant="rectangular" height={40} />
          </Grid>
        ))}
      </Grid>
    )
  }

  if (variant === 'actionButtons') {
    return (
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} variant="rectangular" width={100} height={36} />
        ))}
      </Box>
    )
  }

  if (variant === 'collapsibleSection') {
    return (
      <Card sx={{ mb: 2 }}>
        <CardHeader
          avatar={<Skeleton variant="circular" width={32} height={32} />}
          title={<Skeleton width={120} />}
          action={<Skeleton variant="circular" width={24} height={24} />}
        />
        <CardContent>
          <Grid container spacing={2}>
            {Array.from({ length: 4 }).map((_, i) => (
              <Grid size={{ xs: 12, md: 6 }} key={i}>
                <Skeleton variant="rectangular" height={40} />
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    )
  }

  if (variant === 'userPrimaryInfo') {
    return (
      <Grid container spacing={3}>
        {/* Left Column */}
        <Grid size={{ xs: 12, md: 6 }}>
          {/* Name Section */}
          <Card sx={{ mb: 2 }}>
            <CardHeader
              avatar={<Skeleton variant="circular" width={24} height={24} />}
              title={<Skeleton width={60} />}
              action={<Skeleton variant="circular" width={20} height={20} />}
            />
            <CardContent>
              <Grid container spacing={2}>
                <Grid size={3}>
                  <Skeleton variant="rectangular" height={40} />
                </Grid>
                <Grid size={4}>
                  <Skeleton variant="rectangular" height={40} />
                </Grid>
                <Grid size={2}>
                  <Skeleton variant="rectangular" height={40} />
                </Grid>
                <Grid size={3}>
                  <Skeleton variant="rectangular" height={40} />
                </Grid>
                <Grid size={12}>
                  <Skeleton variant="rectangular" height={40} />
                </Grid>
                <Grid size={12}>
                  <Skeleton width={180} height={16} />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Login Credentials */}
          <Card sx={{ mb: 2 }}>
            <CardHeader
              avatar={<Skeleton variant="circular" width={24} height={24} />}
              title={<Skeleton width={120} />}
              action={<Skeleton variant="circular" width={20} height={20} />}
            />
            <CardContent>
              <Grid container spacing={2}>
                <Grid size={6}>
                  <Skeleton variant="rectangular" height={40} />
                </Grid>
                <Grid size={6}>
                  <Box>
                    <Skeleton width={80} height={16} sx={{ mb: 1 }} />
                    <Skeleton variant="rectangular" height={32} width={140} />
                  </Box>
                </Grid>
                <Grid size={6}>
                  <Skeleton variant="rectangular" height={40} />
                </Grid>
                <Grid size={6}>
                  <Skeleton variant="rectangular" height={40} />
                </Grid>
                <Grid size={12}>
                  <Box>
                    <Skeleton width={100} height={16} sx={{ mb: 1 }} />
                    <Skeleton variant="rectangular" height={32} width={100} />
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Company */}
          <Card sx={{ mb: 2 }}>
            <CardHeader
              avatar={<Skeleton variant="circular" width={24} height={24} />}
              title={<Skeleton width={80} />}
              action={<Skeleton variant="circular" width={20} height={20} />}
            />
            <CardContent>
              <Skeleton variant="rectangular" height={40} />
            </CardContent>
          </Card>

          {/* Account Settings */}
          <Card sx={{ mb: 2 }}>
            <CardHeader
              avatar={<Skeleton variant="circular" width={24} height={24} />}
              title={<Skeleton width={120} />}
              action={<Skeleton variant="circular" width={20} height={20} />}
            />
            <CardContent>
              <Stack spacing={2}>
                <Skeleton width={60} height={16} />
                <Skeleton variant="rectangular" height={56} />
                <Skeleton width={140} height={16} />
                <Stack spacing={1}>
                  {Array.from({ length: 3 }).map((_, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Skeleton variant="rectangular" width={20} height={20} />
                      <Skeleton width={150} />
                    </Box>
                  ))}
                </Stack>
                <Box>
                  <Skeleton width={120} height={16} sx={{ mb: 1 }} />
                  <Skeleton variant="rectangular" height={32} width={100} />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column */}
        <Grid size={{ xs: 12, md: 6 }}>
          {/* Contact */}
          <Card sx={{ mb: 2 }}>
            <CardHeader
              avatar={<Skeleton variant="circular" width={24} height={24} />}
              title={<Skeleton width={60} />}
              action={<Skeleton variant="circular" width={20} height={20} />}
            />
            <CardContent>
              <Grid container spacing={2}>
                {Array.from({ length: 3 }).map((_, i) => (
                  <Grid size={12} key={i}>
                    <Skeleton variant="rectangular" height={40} />
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>

          {/* Address */}
          <Card sx={{ mb: 2 }}>
            <CardHeader
              avatar={<Skeleton variant="circular" width={24} height={24} />}
              title={<Skeleton width={60} />}
              action={<Skeleton variant="circular" width={20} height={20} />}
            />
            <CardContent>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <Skeleton variant="rectangular" height={40} />
                </Grid>
                <Grid size={12}>
                  <Skeleton variant="rectangular" height={40} />
                </Grid>
                <Grid size={6}>
                  <Skeleton variant="rectangular" height={40} />
                </Grid>
                <Grid size={6}>
                  <Skeleton variant="rectangular" height={40} />
                </Grid>
                <Grid size={12}>
                  <Skeleton variant="rectangular" height={40} />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Advantage */}
          <Card sx={{ mb: 2 }}>
            <CardHeader
              avatar={<Skeleton variant="circular" width={24} height={24} />}
              title={<Skeleton width={80} />}
              action={<Skeleton variant="circular" width={20} height={20} />}
            />
            <CardContent>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <Skeleton variant="rectangular" height={40} />
                </Grid>
                <Grid size={12}>
                  <Skeleton variant="rectangular" height={40} />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Comments */}
          <Card sx={{ mb: 2 }}>
            <CardHeader
              avatar={<Skeleton variant="circular" width={24} height={24} />}
              title={<Skeleton width={80} />}
              action={<Skeleton variant="circular" width={20} height={20} />}
            />
            <CardContent>
              <Skeleton variant="rectangular" height={100} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    )
  }

  if (variant === 'companyPrimaryInfo') {
    return (
      <Grid container spacing={3}>
        {/* Left Column */}
        <Grid size={{ xs: 12, md: 6 }}>
          {/* Company Information */}
          <Card sx={{ mb: 2 }}>
            <CardHeader
              avatar={<Skeleton variant="circular" width={24} height={24} />}
              title={<Skeleton width={140} />}
              action={<Skeleton variant="circular" width={20} height={20} />}
            />
            <CardContent>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <Skeleton variant="rectangular" height={40} />
                </Grid>
                <Grid size={12}>
                  <Skeleton variant="rectangular" height={40} />
                </Grid>
                <Grid size={12}>
                  <Skeleton variant="rectangular" height={40} />
                </Grid>
                <Grid size={12}>
                  <Skeleton width={180} height={16} />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Company Settings */}
          <Card sx={{ mb: 2 }}>
            <CardHeader
              avatar={<Skeleton variant="circular" width={24} height={24} />}
              title={<Skeleton width={120} />}
              action={<Skeleton variant="circular" width={20} height={20} />}
            />
            <CardContent>
              <Stack spacing={2}>
                <Box>
                  <Skeleton width={80} height={16} sx={{ mb: 1 }} />
                  <Skeleton variant="rectangular" height={32} width={140} />
                </Box>
                <Box>
                  <Skeleton width={100} height={16} sx={{ mb: 1 }} />
                  <Skeleton variant="rectangular" height={32} width={100} />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column */}
        <Grid size={{ xs: 12, md: 6 }}>
          {/* Contact Information */}
          <Card sx={{ mb: 2 }}>
            <CardHeader
              avatar={<Skeleton variant="circular" width={24} height={24} />}
              title={<Skeleton width={140} />}
              action={<Skeleton variant="circular" width={20} height={20} />}
            />
            <CardContent>
              <Grid container spacing={2}>
                {Array.from({ length: 4 }).map((_, i) => (
                  <Grid size={12} key={i}>
                    <Skeleton variant="rectangular" height={40} />
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>

          {/* Address */}
          <Card sx={{ mb: 2 }}>
            <CardHeader
              avatar={<Skeleton variant="circular" width={24} height={24} />}
              title={<Skeleton width={60} />}
              action={<Skeleton variant="circular" width={20} height={20} />}
            />
            <CardContent>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <Skeleton variant="rectangular" height={40} />
                </Grid>
                <Grid size={12}>
                  <Skeleton variant="rectangular" height={40} />
                </Grid>
                <Grid size={6}>
                  <Skeleton variant="rectangular" height={40} />
                </Grid>
                <Grid size={6}>
                  <Skeleton variant="rectangular" height={40} />
                </Grid>
                <Grid size={12}>
                  <Skeleton variant="rectangular" height={40} />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Comments */}
          <Card sx={{ mb: 2 }}>
            <CardHeader
              avatar={<Skeleton variant="circular" width={24} height={24} />}
              title={<Skeleton width={80} />}
              action={<Skeleton variant="circular" width={20} height={20} />}
            />
            <CardContent>
              <Skeleton variant="rectangular" height={100} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    )
  }

  return null
}